import { Button, Dialog } from '@mui/material'
import React, { useEffect, useState } from 'react'
import colorConfigs from '../../../configs/colorConfigs'
import ReactQuill from 'react-quill'
import { useTheme } from '../../../components/hooks/useTheme'
import useConfirm from '../../../components/hooks/useConfirm'
import { send_Receipt, send_RentalAgreement } from '../../../services/reservation'
import PleaseWaitPopup from '../../PleaseWaitPopup'
import useGeneral from '../../../components/hooks/useGeneral'
import EmailValidatedComponent from '../../../components/EmailValidatedComponent'
import Cookies from 'js-cookie'
import useTenant from '../../../components/hooks/useTenant'
import useAuth from '../../../components/hooks/useAuth'

const modules = {
    toolbar: [
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
        ["link", "image", "video"],
        ["clean"],
    ],
};

type SendEmailPopupProps = {
    open: boolean;
    onClose: () => void;
    emailType: string;
    reservationId: number;
    isVehicleReplaced: boolean;
    bookingNumber: string;
    customerName: string;
    customerEmail: string;
}

const SendEmailPopup = ({
    open,
    onClose,
    emailType,
    reservationId,
    isVehicleReplaced,
    bookingNumber,
    customerName,
    customerEmail
}: SendEmailPopupProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { theme, themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { generalSetting }: any = useGeneral();
    const { handleTenantError }: any = useTenant();

    const [toEmail, setToEmail] = useState<string>("");
    const [ccEmail, setCcEmail] = useState<string>("");
    const [replyemail, setReplyEmail] = useState<string>("");
    const [subject, setSubject] = useState<string>("");
    const [content, setContent] = useState<string>("");
    const [pleaseWaitPopup, setpleaseWaitPopup] = useState<boolean>(false);
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    useEffect(() => {
        if (open) {
            if (emailType === "Agreement") {
                setSubject(`Rental Agreement #${bookingNumber || "-"} - ${customerName}`);
            } else {
                setSubject(`Receipt #${bookingNumber || "-"} - ${customerName}`);
            }

            setToEmail(customerEmail);
            setReplyEmail(generalSetting?.replyToEmail);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reservationId, generalSetting])

    const resetForm = () => {
        onClose();
        setToEmail("");
        setCcEmail("");
        setReplyEmail("");
        setSubject("");
        setContent("");
    }

    const handleSendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await setConfirmData({
            title: 'Send Email',
            summary: "Are you sure you want to send the email?",
            confirmBtn: 'Yes',
            isConfirm: async (callback: any) => {
                callback();

                setpleaseWaitPopup(true);
                if (emailType === "Agreement") {
                    await send_RentalAgreement({
                        reservatinId: reservationId,
                        isReplaced: isVehicleReplaced || false,
                        toEmail: toEmail || "",
                        ccEmail: ccEmail || "",
                        replyEmail: replyemail || "",
                        subject: subject || "",
                        comment: content || ""
                    }, auth?.id, tenantId)
                        .then(() => {
                            resetForm();
                            setpleaseWaitPopup(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setpleaseWaitPopup(false);
                            handleTenantError(error);
                        });
                } else {
                    await send_Receipt({
                        reservatinId: reservationId,
                        isReplaced: isVehicleReplaced || false,
                        toEmail: toEmail || "",
                        ccEmail: ccEmail || "",
                        replyEmail: replyemail || "",
                        subject: subject || "",
                        comment: content || ""
                    }, auth?.id, tenantId)
                        .then(() => {
                            resetForm();
                            setpleaseWaitPopup(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setpleaseWaitPopup(false);
                            handleTenantError(error);
                        });
                }
            }
        });

        setConfirmPopup(true);
    }

    return (
        <>
            <Dialog
                open={open}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '600px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => onClose()}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Send Email</h2>
                                    </div>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <form onSubmit={handleSendEmail}>
                                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>To</label>
                                                    <EmailValidatedComponent
                                                        text={toEmail}
                                                        setText={setToEmail}
                                                        required={true}
                                                    />
                                                </div>

                                                <div />

                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>CC</label>
                                                    <EmailValidatedComponent
                                                        text={ccEmail}
                                                        setText={setCcEmail}
                                                    />
                                                </div>

                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Reply To</label>
                                                    <EmailValidatedComponent
                                                        text={replyemail}
                                                        setText={setReplyEmail}
                                                    />
                                                </div>

                                                <div className="col-span-2">
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Subject</label>
                                                    <input
                                                        type="text"
                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                        value={subject}
                                                        onChange={(e) => setSubject(e.target.value)}
                                                        required
                                                    />
                                                </div>

                                                <div className="col-span-2 relative">
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Content</label>
                                                    <ReactQuill
                                                        style={{ height: "150px", width: "100%" }}
                                                        theme="snow"
                                                        value={content}
                                                        modules={modules}
                                                        onChange={setContent}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex gap-4 mt-[6rem]">
                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={onClose}
                                                >
                                                    Cancel
                                                </Button>

                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.main.bg
                                                    }}
                                                    type="submit"
                                                >
                                                    Send Email
                                                </Button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
        </>
    )
}

export default SendEmailPopup